import { useCallback, useMemo } from 'react';
import {
  HOUSEHOLD_DOC,
  WORK_SCHEDULE_PROFILES_KEY,
  parseWorkScheduleProfile,
  type WorkScheduleProfile,
} from '@awwyeah/shared';
import { useYDoc, useYMap } from '../../sync';

/**
 * Per-member import profiles live in the household doc so every device agrees on who has
 * one (the digest shortcut keys off that) and on the house rules — a rule fixed on Dan's
 * phone is fixed on Kora's. One profile per member; a whole-object LWW register, which is
 * fine for a record edited by one person at a time.
 */
export function useWorkScheduleProfiles(): {
  profiles: Map<string, WorkScheduleProfile>;
  saveProfile: (profile: WorkScheduleProfile) => void;
} {
  const doc = useYDoc(HOUSEHOLD_DOC);
  const { map, entries } = useYMap<WorkScheduleProfile>(doc, WORK_SCHEDULE_PROFILES_KEY);

  const profiles = useMemo(() => {
    const out = new Map<string, WorkScheduleProfile>();
    for (const [id, value] of entries) {
      const profile = parseWorkScheduleProfile(value);
      if (profile) out.set(id, profile);
    }
    return out;
  }, [entries]);

  const saveProfile = useCallback(
    (profile: WorkScheduleProfile) => {
      map.set(profile.memberId, profile);
    },
    [map],
  );

  return { profiles, saveProfile };
}
